import { useCallback, useMemo } from 'react';
import { tryCatchAsync } from 'new/utils/TryCatch';
import useAppDispatch from 'js/hooks/useAppDispatch';
import {
  clearFlowRunLogs,
  clearFlowRunLogsWithFacetsV2,
  fetchFlowRunLogs,
  fetchFlowRunLogsMetadata,
  fetchFlowRunLogsWithoutRunId,
} from 'js/actions/metrics';
import {
  fetchFlowRunLogsWithFacets,
  fetchFlowRunLogsWithFacetsAutoRefresh,
  Options as FetchFlowRunLogsWithFacetsOptions,
} from 'js/actions/metrics/flowRunLogsWithFacets';
import { buildResponse } from 'js/components/data_flows/cards/FlowRunLogs/utils/buildResponse';

type ResponseType = {
  success: boolean;
  data: any;
  error: any;
};

export type UseFlowRunsLogsAdapterReturn = {
  clearLogs: () => Promise<any>;
  fetchFlowRunLogsPage: (options: any) => Promise<ResponseType>;
  fetchFlowRunLogsMetadata: (options: any) => Promise<ResponseType>;
  fetchFlowRunLogsWithFacets: (options: FetchFlowRunLogsWithFacetsOptions) => Promise<ResponseType>;
  fetchFlowRunLogsWithFacetsAutoRefresh: (
    options: FetchFlowRunLogsWithFacetsOptions,
  ) => Promise<ResponseType>;
};

export const useAdapter = (originNodeId: string): UseFlowRunsLogsAdapterReturn => {
  const dispatch = useAppDispatch();

  const _clearLogs = useCallback(async () => {
    dispatch(clearFlowRunLogs());
    dispatch(clearFlowRunLogsWithFacetsV2());
  }, [dispatch]);

  const _fetchFlowRunLogsPage = useCallback(
    async (options: any) => {
      const thunkAction = options.runId
        ? fetchFlowRunLogs(originNodeId, options)
        : fetchFlowRunLogsWithoutRunId(originNodeId, options);

      return buildResponse(await tryCatchAsync(dispatch(thunkAction)));
    },
    [originNodeId],
  );

  const _fetchFlowRunLogsMetadata = useCallback(
    async (options: any) => {
      const thunkAction = fetchFlowRunLogsMetadata(originNodeId, options);
      return buildResponse(await tryCatchAsync(dispatch(thunkAction)));
    },
    [originNodeId],
  );

  const _fetchFlowRunLogsWithFacets = useCallback(
    async (options: FetchFlowRunLogsWithFacetsOptions) => {
      const thunkAction = fetchFlowRunLogsWithFacets(originNodeId, options);
      const response = await tryCatchAsync(dispatch(thunkAction));
      return buildResponse(response);
    },
    [originNodeId],
  );

  const _fetchFlowRunLogsWithFacetsAutoRefresh = useCallback(
    async (options: FetchFlowRunLogsWithFacetsOptions) => {
      const thunkAction = fetchFlowRunLogsWithFacetsAutoRefresh(originNodeId, options);
      const response = await tryCatchAsync(dispatch(thunkAction));
      return buildResponse(response);
    },
    [originNodeId],
  );

  const actions = useMemo(
    () => ({
      clearLogs: _clearLogs,
      fetchFlowRunLogsPage: _fetchFlowRunLogsPage,
      fetchFlowRunLogsMetadata: _fetchFlowRunLogsMetadata,
      fetchFlowRunLogsWithFacets: _fetchFlowRunLogsWithFacets,
      fetchFlowRunLogsWithFacetsAutoRefresh: _fetchFlowRunLogsWithFacetsAutoRefresh,
    }),
    [
      _clearLogs,
      _fetchFlowRunLogsPage,
      _fetchFlowRunLogsMetadata,
      _fetchFlowRunLogsWithFacets,
      _fetchFlowRunLogsWithFacetsAutoRefresh,
    ],
  );

  return actions as UseFlowRunsLogsAdapterReturn;
};
